// Quote dashboard — the customer's view-only list of own + imported quotes
// (past + in-progress). Per-row actions are "Reorder / use as starting point"
// (server-side clone — a distinct behavior from starting blank) and Delete /
// Discard; the order title itself is the link that opens it, so no separate Open
// button. Starting a *new* order is NOT an action here: that single primary
// action lives in the top nav (see StartNewOrderButton). This page only shows
// historical / in-progress orders. A search box + status filter + "Show more"
// paging keep a long import history manageable — all client-side over the
// already-fetched list (list metadata only; no pricing data is cached, per the
// no-caching rule).
//
// This is where a customer counts their orders, so two distinctions have to be
// legible on the row itself:
//   • WHICH order Argo's quote is linked to — marked only where Nexus confirmed
//     the link (erpLinkConfirmedAt), never on a binding Nexus has not echoed.
//   • WHICH orders carry no work yet — those read "Not started", not "In
//     progress" (rowStatus), and their per-row action is a discard.
// Neither is filtered out of the list; a separate reaper handles cleanup. The job
// here is only that the rows read correctly.

import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import i18n from 'i18next';
import { Trans, useTranslation } from 'react-i18next';

import { isEmptyDraft, type QuoteListItemDto } from 'shared';

import * as quotesApi from '../api/quotes.js';
import { ANALYTICS_EVENTS, trackEvent } from '../lib/analytics/index.js';
import { NEW_ORDER_PATH } from '../components/StartNewOrderButton.js';
import { formatCurrency, formatDate, formatNumber } from '../i18n/formatters.js';
import { quoteStatusLabel } from '../i18n/quoteStatus.js';

// Non-component helper — money formatting only, no translated copy involved,
// so no i18next key needed here (unlike quoteStatusLabel).
function formatTotal(q: QuoteListItemDto): string {
  if (q.total_sale_amount == null) return '—';
  const n = Number(q.total_sale_amount);
  return Number.isFinite(n) ? formatCurrency(n, q.currency ?? 'USD') : '—';
}

// Semantic pill treatment per the status (ui-review §4.12).
function statusVariant(status: string): 'neutral' | 'quiet' | 'success' | 'warn' | 'error' {
  switch (status) {
    case 'approved':
    case 'submitted':
    case 'paid': return 'success';
    case 'pending_approval': return 'warn';
    case 'declined':
    case 'cancelled': return 'error';
    default: return 'neutral';
  }
}

// Pill for one row — the status treatment above, except that an order with
// nothing in it (shared isEmptyDraft: a draft, no items, never priced) reads
// "Not started" on the quiet variant. `draft` alone renders as "In progress",
// which claims work that hasn't happened and makes an order the customer never
// touched indistinguishable from the one they are filling in. isEmptyDraft is the
// same definition the reaper sweeps on, so the surface and the sweep cannot
// disagree about what counts as empty.
function rowStatus(q: QuoteListItemDto): { variant: string; label: string } {
  if (isEmptyDraft(q)) {
    return { variant: 'quiet', label: i18n.t('status:list.status.notStarted') };
  }
  return { variant: statusVariant(q.status), label: quoteStatusLabel(q.status) };
}

// When Nexus last confirmed THIS order is the one its Opportunity is linked to,
// or null. A `nexus_opportunity_id` with no confirmation is an orphaned binding —
// Nexus is pointing at some other order — and gets no marker at all: claiming a
// link the ERP never echoed back is worse than saying nothing. Even when set,
// this is the portal's binding plus the last echo it received, not a live read of
// the ERP, which is why the marker is dated rather than stated in the present
// tense.
function erpLinkConfirmedAt(q: QuoteListItemDto): string | null {
  return q.nexus_opportunity_id !== null ? q.nexus_link_confirmed_at : null;
}

// Fixed status progression (not alphabetical — a deliberate order, which the
// sort-alphabetically convention explicitly exempts). Only statuses actually
// present in the list are offered in the filter.
const STATUS_ORDER = [
  'draft',
  'priced',
  'pending_approval',
  'approved',
  'submitted',
  'paid',
  'declined',
  'cancelled',
];

const PAGE_SIZE = 10;

export function QuoteList(): JSX.Element {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { t } = useTranslation(['status', 'common']);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['quotes'],
    queryFn: quotesApi.listQuotes,
  });

  async function handleClone(id: string): Promise<void> {
    const created = await quotesApi.cloneQuote(id);
    trackEvent(ANALYTICS_EVENTS.quote_cloned);
    await queryClient.invalidateQueries({ queryKey: ['quotes'] });
    navigate(`/quotes/${created.id}`);
  }

  // One removal path for every row (soft delete server-side). An order with
  // nothing in it is framed as discarding a false start rather than deleting
  // work, because that is what it is.
  async function handleDelete(q: QuoteListItemDto): Promise<void> {
    if (!confirm(isEmptyDraft(q) ? t('list.confirmDiscard') : t('list.confirmDelete'))) {
      return;
    }
    await quotesApi.deleteQuote(q.id);
    await refetch();
    // Discarding the order a Nexus deal was bound to leaves that deal with none,
    // so the entry screen must stop naming it as still in progress.
    await queryClient.invalidateQueries({ queryKey: quotesApi.entryContextQuery.queryKey });
  }

  // Statuses present in the data, in progression order, for the filter select.
  const availableStatuses = useMemo(() => {
    const present = new Set((data ?? []).map((q) => q.status));
    return STATUS_ORDER.filter((s) => present.has(s as QuoteListItemDto['status']));
  }, [data]);

  // Filter (title search + status) then sort most-recently-updated first.
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (data ?? [])
      .filter((q) => {
        if (statusFilter !== 'all' && q.status !== statusFilter) return false;
        if (!term) return true;
        // A rep searches by the Nexus quote number as often as by the name.
        return [q.project_title ?? t('list.untitledOrder'), q.nexus_quote_number ?? '']
          .some((text) => text.toLowerCase().includes(term));
      })
      .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
  }, [data, search, statusFilter, t]);

  const visible = filtered.slice(0, visibleCount);
  const remaining = filtered.length - visible.length;

  function handleSearchChange(value: string): void {
    setSearch(value);
    setVisibleCount(PAGE_SIZE);
  }

  function handleStatusChange(value: string): void {
    setStatusFilter(value);
    setVisibleCount(PAGE_SIZE);
  }

  return (
    <div className="card">
      <h2>{t('list.title')}</h2>

      {isLoading && <p style={{ marginTop: 16 }}>{t('common:states.loading')}</p>}

      {error && (
        <div className="banner banner--error" style={{ marginTop: 16 }}>
          {t('list.error.loadFailed')}
        </div>
      )}

      {data && data.length === 0 && (
        <p style={{ marginTop: 16 }}>
          <Trans
            i18nKey="list.empty.body"
            t={t}
            components={[<Link key="new" to={NEW_ORDER_PATH} />]}
          />
        </p>
      )}

      {data && data.length > 0 && (
        <>
          <div className="quote-toolbar">
            <input
              type="search"
              className="quote-toolbar__search"
              placeholder={t('list.toolbar.searchLabel')}
              aria-label={t('list.toolbar.searchLabel')}
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
            {availableStatuses.length > 1 && (
              <label className="quote-toolbar__filter">
                <span className="quote-toolbar__filter-label">{t('list.toolbar.statusLabel')}</span>
                <select
                  value={statusFilter}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  aria-label={t('list.toolbar.filterAria')}
                >
                  <option value="all">{t('list.toolbar.allStatuses')}</option>
                  {availableStatuses.map((s) => (
                    <option key={s} value={s}>{quoteStatusLabel(s)}</option>
                  ))}
                </select>
              </label>
            )}
            <span className="quote-toolbar__count">
              {filtered.length === data.length
                ? t('list.toolbar.countAll', { count: data.length })
                : t('list.toolbar.countFiltered', { filtered: filtered.length, total: data.length })}
            </span>
          </div>

          {filtered.length === 0 ? (
            <p className="quote-empty-filter">
              <Trans
                i18nKey="list.emptyFilter.body"
                t={t}
                components={[
                  <button
                    key="clear"
                    type="button"
                    className="link-button"
                    onClick={() => {
                      handleSearchChange('');
                      handleStatusChange('all');
                    }}
                  />,
                ]}
              />
            </p>
          ) : (
            <table className="quote-table">
              <thead>
                <tr>
                  <th>{t('list.table.order')}</th>
                  <th>{t('list.table.status')}</th>
                  <th className="num">{t('list.table.projects')}</th>
                  <th className="num">{t('list.table.total')}</th>
                  <th>{t('list.table.updated')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {visible.map((q) => {
                  const orderTitle = q.project_title ?? t('list.untitledOrder');
                  const pill = rowStatus(q);
                  const notStarted = isEmptyDraft(q);
                  const linkConfirmedAt = erpLinkConfirmedAt(q);
                  return (
                    <tr key={q.id} className={notStarted ? 'quote-table__row--not-started' : undefined}>
                      <td>
                        <Link to={`/quotes/${q.id}`} className="quote-table__title">
                          {orderTitle}
                        </Link>
                        {/* The bound Opportunity's quote number when Nexus has
                            given it, else the plain linked marker. */}
                        {q.nexus_quote_number ? (
                          <span
                            className="tag tag--linked"
                            {...(linkConfirmedAt !== null
                              ? {
                                  title: t('list.tag.erpLinkedTitle', {
                                    date: formatDate(linkConfirmedAt),
                                  }),
                                }
                              : {})}
                          >
                            {t('approval.quoteNumberLabel', { number: q.nexus_quote_number })}
                          </span>
                        ) : (
                          linkConfirmedAt !== null && (
                            <span
                              className="tag tag--linked"
                              title={t('list.tag.erpLinkedTitle', {
                                date: formatDate(linkConfirmedAt),
                              })}
                            >
                              {t('list.tag.erpLinked')}
                            </span>
                          )
                        )}
                        {q.is_historical && <span className="tag tag--historical">{t('list.tag.pastOrder')}</span>}
                        {q.is_partial && <span className="tag tag--partial">{t('list.tag.needsReview')}</span>}
                      </td>
                      <td>
                        <span className={`status-pill status-pill--${pill.variant}`}>
                          {pill.label}
                        </span>
                      </td>
                      <td className="num">{formatNumber(q.line_count)}</td>
                      <td className="num">{formatTotal(q)}</td>
                      <td>{formatDate(q.updated_at)}</td>
                      <td className="actions">
                        {/* Reordering an order with nothing in it would just mint
                            another empty one, so it isn't offered there. */}
                        {!notStarted && (
                          <button
                            type="button"
                            onClick={() => void handleClone(q.id)}
                            title={t('list.actions.reorderTitle')}
                          >
                            {t('list.actions.reorder')}
                          </button>
                        )}
                        <button
                          type="button"
                          className="icon-button icon-button--danger"
                          onClick={() => void handleDelete(q)}
                          aria-label={
                            notStarted
                              ? t('list.actions.discardAria', { title: orderTitle })
                              : t('list.actions.deleteAria', { title: orderTitle })
                          }
                          title={notStarted ? t('list.actions.discardTitle') : t('list.actions.deleteTitle')}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            <line x1="10" y1="11" x2="10" y2="17" />
                            <line x1="14" y1="11" x2="14" y2="17" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {remaining > 0 && (
            <button
              type="button"
              className="quote-show-more"
              onClick={() => setVisibleCount((n) => n + PAGE_SIZE)}
            >
              {t('list.showMore.button', { count: Math.min(remaining, PAGE_SIZE) })}
              {remaining > PAGE_SIZE && t('list.showMore.remainingSuffix', { count: remaining })}
            </button>
          )}
        </>
      )}
    </div>
  );
}
